import { useEffect, useState } from "react";
import styled from "styled-components";
import { waitForTransaction } from "@wagmi/core";
import { OPX_ADDRESS } from "../constants";
import ABI from "../abis/opx-abi.json";
import { Button, Input, message, Popconfirm } from "antd";
import { useAccount, useContract, useSigner } from "wagmi";
import { shortenAddress } from "../utils/shortenAddress";
import { checkAddress } from "../utils/checkAddress";
import { handleCopyClick } from "../utils/copyToClipboard";
import numberToStringWithSpace from "../utils/numberToStringWithSpace";

const Wraper = styled.div`
  cursor: default;
  font-family: Inter, sans-serif;
  max-width: 21.25rem;
  position: relative;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  width: 100%;
  margin: auto;
  padding: 20px;
`;

const Body = styled.div`
  background-color: #131315;
  border-radius: 0.75rem;
  padding: 1.5rem;
  position: relative;
  width: 100%;
  z-index: 2;
`;

const Monitor = styled.div`
  margin: auto;
  border-radius: 0.75rem;
  position: relative;
  padding: 10px 0;
  width: 100%;
  border: 1px solid;
  height: fit-content;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;

  text {
    font-size: 30px;
    padding: 5px;
    overflow-x: hidden;
  }
`;

const BoxBtn = styled.div`
  margin: auto;
  position: relative;
  padding: 10px 0;
  width: 100%;
  height: fit-content;
  min-height: 100px;
  display: flex;
  max-width: 300px;
  flex-wrap: wrap;
  h2 {
    line-height: 100%;
    margin: auto;
  }
`;

export const EvmPage = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const { data: signer, isError, isLoading } = useSigner();
  const { address } = useAccount();
  const [totalSupply, setTotalSupply] = useState<number>();
  const [decimals, setDecimals] = useState<number>(0);
  const [owner, setOwner] = useState<string>("");
  const [symbol, setSymbol] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [paused, setPaused] = useState<boolean>(false);

  const contract = useContract({
    address: OPX_ADDRESS,
    abi: ABI,
    signerOrProvider: signer,
  });

  const isBlocked = async () => {
    const address = (
      document.getElementById("isBlockedAddress") as HTMLInputElement
    ).value;

    if (checkAddress(address)) {
      const res = await contract?.isBlocked(address);
      let isBlocked = document.getElementById("isBlocked");
      (isBlocked as HTMLElement).innerHTML = res;
      messageApi.success(
        `${shortenAddress(address)} is ${
          res === true ? "in Black List" : "not in Black List"
        }`
      );
    } else {
      message.error("Address is incorrect");
    }
  };

  const toBlackList = async () => {
    const address = (document.getElementById("toBlackList") as HTMLInputElement)
      .value;
    try {
      if (checkAddress(address)) {
        const res = await contract?.toBlackList(address);
        messageApi.loading(`Transaction ${shortenAddress(res.hash)} sendet`);
        waitForTransaction({ hash: res.hash }).then((tx) => {
          message.success(
            `Address ${shortenAddress(address)} addet to Black List`
          );
        });
      } else {
        message.error("Address is incorrect");
      }
    } catch (err) {
      console.log(err);
      message.error("Error " + err);
    }
  };

  const fromBlackList = async () => {
    let address = (document.getElementById("fromBlackList") as HTMLInputElement)
      .value;
    if (checkAddress(address)) {
      try {
        const res = await contract?.fromBlackList(address);
        await waitForTransaction({ hash: res.hash });
      } catch (err: any) {
        console.log(err);
        messageApi.error("Transaction Error " + err);
      }
    } else {
      message.error("Address is incorrect");
    }
  };

  const getTotalSupply = async () => {
    const res = await contract?.totalSupply();
    setTotalSupply(Number(res));
  };
  const getInfo = async () => {
    getTotalSupply();
    const owner = await contract?.owner();
    if (owner) {
      setOwner(owner);
    }
    const symbol = await contract?.symbol();
    if (symbol) {
      setSymbol(symbol);
    }
    const name = await contract?.name();
    if (symbol) {
      setName(name);
    }
    const paused = await contract?.paused();
    if (paused) {
      setPaused(paused);
    }
    const decimals = await contract?.decimals();
    if (decimals) {
      setDecimals(decimals);
    }
  };

  const pause = async () => {
    try {
      const res = await contract?.pause();
      await waitForTransaction({ hash: res.hash });
      getPaused();
    } catch (err) {
      console.log(err);
      messageApi.error("Error " + err);
    }
  };

  const unpause = async () => {
    try {
      const res = await contract?.unpause();
      await waitForTransaction({ hash: res.hash });
      getPaused();
    } catch (err) {
      console.log(err);
      messageApi.error("Error " + err);
    }
  };

  const getPaused = async () => {
    const res = await contract?.paused();
    setPaused(res);
  };

  const mint = async () => {
    const to = (document.getElementById("mintTo") as HTMLInputElement).value;
    const amount = (document.getElementById("mintAmount") as HTMLInputElement)
      .value;

    if (checkAddress(to) && amount) {
      try {
        const res = await contract?.mint(
          to,
          (Number(amount) * 10 ** decimals).toString()
        );
        await waitForTransaction({ hash: res.hash });
      } catch (err) {
        console.log(err);
        messageApi.error("Error " + err);
      }
    }
  };

  const burn = async () => {
    const amount = (document.getElementById("burnAmount") as HTMLInputElement)
      .value;

    if (amount) {
      const res = await contract?.burn(
        (Number(amount) * 10 ** decimals).toString()
      );
      await waitForTransaction({ hash: res.hash });
    }
  };

  const burnBlackFunds = async () => {
    const address = (document.getElementById("burnFrom") as HTMLInputElement)
      .value;
    const amount = (
      document.getElementById("burnFromAmount") as HTMLInputElement
    ).value;
    try {
      if (checkAddress(address) && amount) {
        const res = await contract?.burnBlackFunds(
          address,
          (Number(amount) * 10 ** decimals).toString()
        );
        await waitForTransaction({ hash: res.hash });
      } else {
        message.error("Address is incorrect");
      }
    } catch (err) {
      console.log(err);
      messageApi.error("Error " + err);
    }
  };

  const transferOwnership = async () => {
    const address = (document.getElementById("newOwner") as HTMLInputElement)
      .value;

    if (checkAddress(address)) {
      const res = await contract?.transferOwnership(address);
      await waitForTransaction({ hash: res.hash });
    } else {
      message.error("Address is incorrect");
    }
  };

  useEffect(() => {
    if (signer) {
      getInfo();
    }
  }, [signer]);
  return (
    <>
      {contextHolder}
      <div
        style={{
          margin: "auto",
          width: "80%",
          maxWidth: "980px",
          display: "flex",
          height: "100px",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: "0px",
          zIndex: "20",
          flexWrap: "wrap",
          gap: "30px",
        }}
      >
        <span>
          {totalSupply
            ? "Total Supply: " +
              numberToStringWithSpace(
                (totalSupply / 10 ** decimals).toFixed(2)
              ) +
              " " +
              symbol
            : " Total Supply: -.--"}
        </span>
        <span>
          Contract:{" "}
          <span
            style={{ cursor: "pointer", color: "green" }}
            onClick={() =>
              handleCopyClick(owner, () => messageApi.success("address copied"))
            }
          >
            {shortenAddress(OPX_ADDRESS)}{" "}
          </span>
        </span>
        <span>Name: {name}</span>
        <span>Decimals: {decimals}</span>
        <span>
          Owner:{" "}
          <span
            onClick={() =>
              handleCopyClick(owner, () => messageApi.success("address copied"))
            }
            style={{
              color: owner === address ? "green" : "red",
              cursor: "pointer",
            }}
          >
            {shortenAddress(owner)}
          </span>
        </span>
        <span>
          {" "}
          paused:{" "}
          <span style={{ color: paused ? "red" : "green" }}>
            {paused ? "pause" : "No"}
          </span>
        </span>
      </div>
      <Wraper>
        <div>
          check address on blacklist
          <Monitor>
            <text id="isBlocked">isBlocked</text>
          </Monitor>
          <Input
            id="isBlockedAddress"
            placeholder="address"
            style={{ margin: "10px 0" }}
            onChange={isBlocked}
          />
          <Button onClick={isBlocked}>call</Button>
        </div>
        <br />
        <div>
          Add Address to BlackList
          <Monitor>
            <text>toBlackList</text>
          </Monitor>
          <Input
            id="toBlackList"
            placeholder="address"
            style={{ margin: "10px 0" }}
          />
          <Popconfirm
            id="popTBL"
            title="Do you want to send user to Black List?"
            onConfirm={
              address === owner
                ? toBlackList
                : () => messageApi.error("you don't have admin rights")
            }
          >
            <Button>call</Button>
          </Popconfirm>
        </div>
        <br />
        <div>
          Remove address from blacklist (fromBlackList)
          <Monitor>
            <text>fromBlackList</text>
          </Monitor>
          <Input
            id="fromBlackList"
            placeholder="address"
            style={{ margin: "10px 0" }}
          />
          <Popconfirm
            id="popFBL"
            title="Do you want to remove user from Black List?"
            onConfirm={
              address === owner
                ? fromBlackList
                : () => messageApi.error("you don't have admin rights")
            }
          >
            <Button>call</Button>
          </Popconfirm>
        </div>

        <br />
        {paused ? (
          <div>
            unpause
            <Monitor>
              <text>unpause</text>
            </Monitor>
            <Popconfirm
              id="popUnpause"
              title="Do you want to unpause all transactions?"
              onConfirm={
                address === owner
                  ? unpause
                  : () => messageApi.error("you don't have admin rights")
              }
            >
              <Button style={{ margin: "10px 0 0 0" }}>call</Button>
            </Popconfirm>
          </div>
        ) : (
          <div>
            pause
            <Monitor>
              <text>pause</text>
            </Monitor>
            <Popconfirm
              id="popPause"
              title="Do you want to pause all transactions?"
              onConfirm={
                address === owner
                  ? pause
                  : () => messageApi.error("you don't have admin rights")
              }
            >
              <Button style={{ margin: "10px 0 0 0" }}>call</Button>
            </Popconfirm>
          </div>
        )}

        <br />
        <div>
          mint
          <Monitor>
            <text>mint</text>
          </Monitor>
          <Input id="mintTo" placeholder="to" style={{ margin: "10px 0" }} />
          <Input
            id="mintAmount"
            placeholder="amount"
            style={{ margin: "0 0 10px 0" }}
          />
          <Popconfirm
            id="popMint"
            title={`Do you want to mint OPX to user?`}
            onConfirm={
              address === owner
                ? mint
                : () => messageApi.error("you don't have admin rights")
            }
          >
            <Button>call</Button>
          </Popconfirm>
        </div>
        <br />
        <div>
          burn
          <Monitor>
            <text>burn</text>
          </Monitor>
          <Input
            id="burnAmount"
            placeholder="amount"
            style={{ margin: "10px 0" }}
          />
          <Popconfirm
            id="popBurn"
            title={`Do you want to burn OPX from you account?`}
            onConfirm={
              address === owner
                ? burn
                : () => messageApi.error("you don't have admin rights")
            }
          >
            <Button>call</Button>
          </Popconfirm>
        </div>
        <br />
        <div>
          burnBlackFunds
          <Monitor>
            <text>burnBlackFunds</text>
          </Monitor>
          <Input
            id="burnFrom"
            placeholder="address"
            style={{ margin: "10px 0" }}
          />
          <Input
            id="burnFromAmount"
            placeholder="amount"
            style={{ margin: "0 0 10px 0" }}
          />
          <Popconfirm
            id="popBBF"
            title={`Do you want to burn OPX from user account?`}
            onConfirm={
              address === owner
                ? burnBlackFunds
                : () => messageApi.error("you don't have admin rights")
            }
          >
            <Button>call</Button>
          </Popconfirm>
        </div>
        <br />
        <div>
          transferOwnership
          <Monitor>
            <text>transferOwnership</text>
          </Monitor>
          <Input id="newOwner" style={{ margin: "10px 0" }} />
          <Popconfirm
            id="popTF"
            title={`WARNING! Do you want to transfer ownership for new Owner?`}
            onConfirm={
              address === owner
                ? transferOwnership
                : () => messageApi.error("you don't have admin rights")
            }
          >
            <Button>call</Button>
          </Popconfirm>
        </div>
      </Wraper>
    </>
  );
};

//  [0x13fedda7d44fbb46b4cb68d0e4716420e5f56239314e021a8573f2875317d44d, 0x03371dd72dda6e0a2e3d9305993a0f9f00f6f1c1be6e4c24b317fbe86e9a930b]
